import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { AtmListItem, AtmTheme } from '../types/atm';

/**
 * Page for exploring public ATMs
 */
const ExploreAtms: React.FC = () => {
  const { publicAtms, loadingPublicAtms, fetchPublicAtms, error } = useAtm();

  // State
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<AtmTheme | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'transactions'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filter and sort ATMs
  const filteredAndSortedAtms = React.useMemo(() => {
    let filtered = publicAtms;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        atm =>
          atm.name.toLowerCase().includes(term) ||
          (atm.description && atm.description.toLowerCase().includes(term)) ||
          (atm.createdByName && atm.createdByName.toLowerCase().includes(term))
      );
    }

    // Filter by theme
    if (selectedTheme !== 'all') {
      filtered = filtered.filter(atm => atm.theme === selectedTheme);
    }

    // Sort ATMs
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'transactions':
          comparison = a.transactionCount - b.transactionCount;
          break;
        case 'date':
        default: {
          // Handle different date formats (Date, Timestamp, string, or null)
          const getDate = (dateValue: any): Date => {
            if (!dateValue) return new Date(0); // Handle null/undefined - use epoch
            if (dateValue instanceof Date) return dateValue;
            if (typeof dateValue === 'string') return new Date(dateValue);
            if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
              return dateValue.toDate();
            }
            return new Date(); // fallback
          };

          const aDate = getDate(a.createdAt);
          const bDate = getDate(b.createdAt);
          comparison = aDate.getTime() - bDate.getTime();
          break;
        }
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [publicAtms, searchTerm, selectedTheme, sortBy, sortDirection]);

  // Format date
  const formatDate = (date: Date | any): string => {
    // Handle different date formats (Date, Timestamp, string, or null)
    let actualDate: Date;
    if (!date) {
      return 'Unknown'; // Handle null/undefined dates
    } else if (date instanceof Date) {
      actualDate = date;
    } else if (typeof date === 'string') {
      actualDate = new Date(date);
    } else if (typeof date === 'object' && typeof date.toDate === 'function') {
      actualDate = date.toDate();
    } else {
      actualDate = new Date(); // fallback
    }

    return actualDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get theme display name
  const getThemeDisplayName = (theme: AtmTheme): string => {
    return theme.charAt(0).toUpperCase() + theme.slice(1);
  };

  // Get theme color
  const getThemeColor = (theme: AtmTheme): string => {
    switch (theme) {
      case AtmTheme.CLASSIC:
        return 'bg-gray-100 text-gray-800';
      case AtmTheme.MODERN:
        return 'bg-purple-100 text-purple-800';
      case AtmTheme.RETRO:
        return 'bg-yellow-100 text-yellow-800';
      case AtmTheme.DARK:
        return 'bg-gray-800 text-white';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Initial data load
  useEffect(() => {
    fetchPublicAtms();
  }, []); // Remove fetchPublicAtms dependency to prevent infinite loop

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Explore ATMs</h1>
          <p className="text-gray-600">Discover and interact with ATMs created by the community</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search ATMs by name, description, or creator..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Theme
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedTheme}
                onChange={e => setSelectedTheme(e.target.value as AtmTheme | 'all')}
              >
                <option value="all">All Themes</option>
                {Object.values(AtmTheme).map(theme => (
                  <option key={theme} value={theme}>
                    {getThemeDisplayName(theme)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort by</label>
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as 'date' | 'name' | 'transactions')}
                >
                  <option value="date">Date Created</option>
                  <option value="name">Name</option>
                  <option value="transactions">Usage</option>
                </select>
                <button
                  type="button"
                  onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
                  className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title={`Sort ${sortDirection === 'desc' ? 'ascending' : 'descending'}`}
                >
                  {sortDirection === 'desc' ? '↓' : '↑'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {!loadingPublicAtms && (
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {filteredAndSortedAtms.length} ATM{filteredAndSortedAtms.length !== 1 ? 's' : ''}{' '}
              found
            </p>
          </div>
        )}

        {/* Loading State */}
        {loadingPublicAtms ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading ATMs...</p>
          </div>
        ) : filteredAndSortedAtms.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            {searchTerm || selectedTheme !== 'all' ? (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No ATMs found</h3>
                <p className="text-gray-600 mb-4">
                  No ATMs match your search criteria. Try adjusting your filters.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTheme('all');
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div>
                <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No public ATMs yet</h3>
                <p className="text-gray-600 mb-6">
                  Be the first to create a public ATM for others to discover and interact with.
                </p>
                <Link
                  to="/create-atm"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Create Public ATM
                </Link>
              </div>
            )}
          </div>
        ) : (
          /* ATM Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedAtms.map((atm: AtmListItem) => (
              <div
                key={atm.id}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/atm/${atm.id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-blue-600 truncate block"
                      >
                        {atm.name}
                      </Link>
                      {atm.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{atm.description}</p>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getThemeColor(
                          atm.theme
                        )}`}
                      >
                        {getThemeDisplayName(atm.theme)}
                      </span>
                    </div>
                  </div>

                  {/* Creator */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500">Created by</p>
                    <p className="text-sm text-gray-900">{atm.createdByName || 'Anonymous User'}</p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Transactions</p>
                      <p className="text-lg font-semibold text-gray-900">{atm.transactionCount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm text-gray-900">{formatDate(atm.createdAt)}</p>
                    </div>
                  </div>

                  {/* Popularity Indicator */}
                  <div className="mb-4">
                    <div className="flex items-center text-xs text-gray-500">
                      <div className="flex-1">
                        {atm.transactionCount === 0 ? (
                          'New'
                        ) : atm.transactionCount < 10 ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-green-500 mr-2 inline-block"></div>
                            Trending
                          </>
                        ) : atm.transactionCount < 50 ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2 inline-block"></div>
                            Popular
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 rounded-full bg-purple-500 mr-2 inline-block"></div>
                            Featured
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/atm/${atm.id}`}
                      className="flex-1 text-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Try ATM
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/atm/${atm.id}`);
                        // Could show a toast notification here
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      title="Copy link"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        {!loadingPublicAtms && filteredAndSortedAtms.length > 0 && (
          <div className="mt-12 text-center">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your Own ATM</h3>
              <p className="text-gray-600 mb-4">
                Join the community and create your own interactive ATM for others to discover.
              </p>
              <Link
                to="/create-atm"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Create ATM
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ExploreAtms;
